import React, { useEffect, useState, forwardRef, useCallback, useMemo, useRef, useImperativeHandle } from 'react';
import { View, Dimensions, Platform, StyleSheet, Pressable } from 'react-native';
import { ReactNativeZoomableView } from '@openspacelabs/react-native-zoomable-view';
import { Button, Text } from 'tamagui';
import { useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { debounce } from 'lodash';
import UserLinkHandler, { UserLinkHandlerHandle } from './LinkHandler';
import DraggableUserCard, { DraggableUserCardHandle, ArrowData } from '@components/Hierarchy/DraggableUserCard';
import UserCard from './UserCard';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import ArrowView from '../utils/ArrowView';
import DepartmentGrouping, { DepartmentLegend, DepartmentGroupingHandle } from './DepartmentGrouping';
import { calculateHierarchicalPositions } from '../utils/departmentPositioning';
import { InfiniteGrid } from '@components/ZoomableGridView';
import HierarchyArrowRenderer from './HierarchyArrowRenderer';
import { getModernCardStyle } from '@stylesheet';
// Interfaces
interface UserProfile {
  name?: string;
  surname?: string;
}

interface User {
  _id: string;
  email?: string;
  profile_information?: UserProfile;
}

interface Department {
  _id: string;
  name: string;
  users?: User[];
}

interface UserPosition {
  x: number;
  y: number;
}

// Enhanced UserHierarchy interface for imperative control
export interface UserHierarchyHandle {
  setComponentPosition: (id: string, x: number, y: number) => void;
  getComponentPosition: (id: string) => { x: number, y: number } | null;
  centerAllComponents: () => void;
  centerView: () => void;
  moveTo: (x: number, y: number) => void;
  zoomTo: (zoom: number, zoomCenter: { x: number, y: number }) => void;
  zoomBy: (delta: number) => void;
  getCurrentZoom: () => number;
  getViewportSize: () => { width: number, height: number };
  forceUpdate: () => void;
}

interface UserHierarchyProps {
  userList: any;
  loadingError?: any;
  departmentList?: any;
  uniqueDepartmentList: any;
  openAddUserModal: () => void;
  openAddToDepartmentModal: (department) => void;
  handleSnap: (target: any, source: any) => void;
  owner: any;
  linkList: any[],
  refresh: () => void;
  companyId: string;
  isLoading?: boolean;
  // Base component to auto-center on
  primaryUser?: User;
  // AcademyCourseEditor pattern: pass initial positions directly
  initialUsers?: Array<{
    _id: string;
    email?: string;
    profile_information?: UserProfile;
    x: number;
    y: number;
    [key: string]: any;
  }>;
  // Callback when user arrow snaps (for showing modals, creating connections)
  onUserSnap?: (targetUserId: string, sourceUserId: string) => void;
  // Callback when user card is clicked (for editing)
  onUserPress?: (user: User) => void;
}

interface Connection {
  from: string;
  to: string;
  type: 'reports_to' | 'manages' | 'collaborates';
}

// Constants
const VIEWPORT_SIZE = { width: 4000, height: 4000 };
const GRID_SIZE = 50;

// Department color mapping
const getDepartmentColor = (departmentName: string) => {
  const colors: Record<string, string> = {
    'Executive': '#8b5cf6',
    'Engineering': '#06b6d4',
    'Sales': '#10b981',
    'Marketing': '#f59e0b',
    'Human Resources': '#ef4444',
    'Reception': '#6B7280',
    'General': '#8B5CF6',
    'Unassigned': '#9CA3AF'
  };
  return colors[departmentName] || '#6b7280';
};

// Department grouping utility
const groupUsersByDepartment = (users: any[]) => {
  const grouped = users.reduce((acc, user) => {
    // Handle both nested user structure (from API) and direct user objects
    // API returns: { user: { profile_information: { department: 'X' } } }
    // Direct user: { department: 'X' } or { profile_information: { department: 'X' } }
    let dept = 'Unassigned';

    if (user.user) {
      // Nested structure from API (company.users or user.connectedUsers)
      // Priority: company.users.department > user.profile_information.department > user.department
      dept = user.department || // ← Company-level department (highest priority)
        user.user.profile_information?.department ||
        user.user.department ||
        'Unassigned';
    } else {
      // Direct user object
      dept = user.department || // ← Company-level department (highest priority)
        user.profile_information?.department ||
        'Unassigned';
    }

    // Ensure users without any department are marked as "Unassigned"
    if (!dept || dept === '' || dept === null || dept === undefined) {
      dept = 'Unassigned';
    }

    if (!acc[dept]) {
      acc[dept] = [];
    }

    // Push the actual user object (unwrap if nested)
    acc[dept].push(user.user || user);
    return acc;
  }, {} as Record<string, any[]>);

  // Sort departments by priority (Executive first, then alphabetically, Unassigned last)
  const priority = ['Executive', 'Engineering', 'Sales', 'Marketing', 'Human Resources', 'Reception'];
  const sortedDepartments = Object.keys(grouped).sort((a, b) => {
    // Unassigned always goes last
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;

    const aIndex = priority.indexOf(a);
    const bIndex = priority.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  return sortedDepartments.map(dept => ({
    name: dept,
    users: grouped[dept],
    color: getDepartmentColor(dept),
    count: grouped[dept].length
  }));
};

// Legacy GridBackground removed - now using InfiniteGrid from ZoomableGridView

// Optimized Connection Lines Component using ArrowView
const ConnectionLines = ({ connections, userPositions, theme, zoomLevel }) => {
  // Disable arrows on Android to prevent bitmap size crashes
  if (Platform.OS === 'android') return null;

  if (!connections || connections.length === 0) return null;

  const getLineColor = (connectionType: string, isOptimistic: boolean = false) => {
    if (isOptimistic) return '#f59e0b'; // Amber for optimistic links

    switch (connectionType) {
      case 'reports_to': return theme ? '#4CAF50' : '#66BB6A';
      case 'manages': return theme ? '#2196F3' : '#42A5F5';
      case 'collaborates': return theme ? '#FF9800' : '#FFA726';
      default: return theme ? '#9E9E9E' : '#BDBDBD';
    }
  };

  return (
    <View style={StyleSheet.absoluteFillObject} pointerEvents="none">
      {connections.map((connection, index) => {
        const fromPos = userPositions[connection.from];
        const toPos = userPositions[connection.to];

        if (!fromPos || !toPos) return null;

        const fromX = fromPos.x + 100; // Center of card
        const fromY = fromPos.y + 60;
        const toX = toPos.x + 100;
        const toY = toPos.y + 60;

        return (
          <ArrowView
            key={`${connection.from}-${connection.to}-${index}`}
            from={{ x: fromX, y: fromY }}
            to={{ x: toX, y: toY }}
            color={getLineColor(connection.type, connection.isOptimistic)}
            width={connection.isOptimistic ? 2 : (connection.type === 'manages' ? 3 : 2)}
            dash={connection.isOptimistic ? [8, 4] : (connection.type === 'collaborates' ? [5, 5] : [])}
            animate={false}
            pressCallback={() => { }}
            data={connection}
            showAvs={false}
            text=""
            showPlusCircle={false}
          />
        );
      })}
    </View>
  );
};

// Main Component with Network Cleanup and Debounced Updates
const UserHierarchy = forwardRef<UserHierarchyHandle, UserHierarchyProps>((props, ref) => {
  const { refresh = () => {}, userList = [], owner, departmentList = [], isLoading = false, initialUsers, primaryUser, onUserPress } = props;

  const [zoomLevel, setZoomLevel] = useState(1);
  const zoomLevelRef = useRef<number>(0.6); // Track real-time zoom
  const [gridOffsetX, setGridOffsetX] = useState(0);
  const [gridOffsetY, setGridOffsetY] = useState(0);
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const [viewportDimensions, setViewportDimensions] = useState<{ width: number; height: number } | null>(null);
  const theme = themeSelector();
  const profileData = useSelector((state: any) => state?.promiseData?.data?.userData);

  // Arrow state management - FULLY ref-based (NO parent state)
  const arrowsRef = useRef<Map<string, ArrowData>>(new Map());
  const arrowRendererRef = useRef<any>(null); // Ref to HierarchyArrowRenderer

  // Get screen dimensions for proper centering
  const screenData = Dimensions.get('window');
  const screenWidth = screenData.width;
  const screenHeight = screenData.height;

  // Start with zero offsets - let moveTo handle all positioning
  const calculatedOffsetX = 0;
  const calculatedOffsetY = 0;

  // console.log('📱 Centering Calculation:', {  // Disabled: too frequent
  //   screen: { width: screenWidth, height: screenHeight, centerX: screenCenterX, centerY: screenCenterY },
  //   viewport: { width: VIEWPORT_SIZE.width, height: VIEWPORT_SIZE.height, centerX: viewportCenterX, centerY: viewportCenterY },
  //   calculated: { offsetX: calculatedOffsetX, offsetY: calculatedOffsetY }
  // });

  // Network cleanup and LinkHandler references
  const networkCleanupRef = useRef<(() => void) | null>(null);
  const linkHandlerRef = useRef<UserLinkHandlerHandle>(null);
  const departmentGroupingRef = useRef<DepartmentGroupingHandle>(null); // Ref to DepartmentGrouping for imperative updates
  const zoomableViewRef = useRef<ReactNativeZoomableView>(null);

  // Store positions in refs to avoid re-renders
  const userPositionsRef = useRef<Record<string, UserPosition>>({});

  // Component position management for imperative control
  const componentPositionsRef = useRef<Map<string, { x: number, y: number }>>(new Map());

  // Refs for draggable components to enable imperative positioning
  const draggableComponentRefs = useRef<Map<string, DraggableUserCardHandle>>(new Map());

  // Track when all refs are populated to trigger positioning
  const [refsReady, setRefsReady] = useState(false);
  const [positioningComplete, setPositioningComplete] = useState(false);
  const [renderKey, setRenderKey] = useState(0); // Force re-render key
  const hasRunImperativePositioning = useRef(false); // Prevent loops


  // Imperative positioning functions
  const setComponentPosition = useCallback((id: string, x: number, y: number) => {
    // Update both position refs
    componentPositionsRef.current.set(id, { x, y });
    userPositionsRef.current[id] = { x, y };

    // Trigger LinkHandler update efficiently using imperative handle
    if (linkHandlerRef.current?.forceUpdate) {
      linkHandlerRef.current.forceUpdate();
    }
  }, []);

  const getComponentPosition = useCallback((id: string): { x: number, y: number } | null => {
    const position = componentPositionsRef.current.get(id) || userPositionsRef.current[id];
    return position || null;
  }, []);




  // AcademyCourseEditor pattern: Use initialUsers if provided, otherwise calculate from userList
  const allUsers = useMemo(() => {
    if (initialUsers && initialUsers.length > 0) {
      // Use provided initial users with positions (AcademyCourseEditor pattern)
      return initialUsers;
    }

    // Fallback: calculate from userList like before
    const safeUserList = Array.isArray(userList) ? userList : [];
    
    // CRITICAL FIX: Unwrap nested user structure from normalizeValues
    // normalizeValues returns: [{ user: {...}, position: {x, y}, ... }] for connectedUsers
    // But it might also return direct user objects if structure is different
    const unwrappedUsers = safeUserList.map((user, index) => {
      // Case 1: User has nested structure (from API/connectedUsers) - { user: {...}, position: {...} }
      if (user.user && user.user._id && typeof user.user === 'object') {
        const unwrapped = {
          ...user.user, // Extract the actual user object
          // Preserve position and other metadata from normalizeValues
          x: user.position?.x,
          y: user.position?.y,
          department: user.department || user.user.profile_information?.department,
          // Preserve other metadata
          role: user.role,
          status: user.status,
          invitations: user.invitations,
          // Preserve the position object for initializePositions
          position: user.position
        };
        
        // If position exists, also set it in refs immediately
        if (user.position && user.position.x !== null && user.position.y !== null) {
          userPositionsRef.current[user.user._id] = { 
            x: user.position.x, 
            y: user.position.y 
          };
          componentPositionsRef.current.set(user.user._id, { 
            x: user.position.x, 
            y: user.position.y 
          });
        }
        
        return unwrapped;
      }
      
      // Case 2: User is already a direct user object but might be missing email/profile_information
      // This can happen if normalizeValues returns the user directly
      if (user._id && !user.user) {
        // Check if this looks like a valid user object
        const hasUserData = user.email || user.profile_information || user.profile_information?.name;
        
        if (hasUserData) {
          // Already a direct user object, just preserve position
          if (user.position && user.position.x !== null && user.position.y !== null) {
            userPositionsRef.current[user._id] = { 
              x: user.position.x, 
              y: user.position.y 
            };
            componentPositionsRef.current.set(user._id, { 
              x: user.position.x, 
              y: user.position.y 
            });
          }
          return user;
        } else {
          // Has _id but no user data - this might be a broken entry
          console.warn('⚠️ User entry missing data:', {
            index,
            _id: user._id,
            keys: Object.keys(user),
            user: user
          });
          return null; // Filter this out
        }
      }
      
      // Case 3: Invalid entry
      console.warn('⚠️ Invalid user entry:', {
        index,
        user,
        hasUser: !!user.user,
        hasId: !!user._id,
        keys: Object.keys(user || {})
      });
      return null;
    }).filter(Boolean); // Remove null entries
    
    console.log('🔍 allUsers processing:', {
      userListLength: safeUserList.length,
      unwrappedUsersLength: unwrappedUsers.length,
      rawUserListSample: safeUserList.slice(0, 3).map(u => ({
        _id: u._id,
        hasUser: !!u.user,
        userId: u.user?._id,
        userEmail: u.user?.email,
        structure: u.user ? 'nested' : 'direct',
        keys: Object.keys(u)
      })),
      unwrappedSample: unwrappedUsers.slice(0, 3).map(u => ({
        _id: u._id,
        email: u.email,
        name: u.profile_information?.name,
        department: u.department || u.profile_information?.department,
        hasNested: !!safeUserList[0]?.user
      }))
    });
    
    const activeUsers = unwrappedUsers.filter(user =>
      user && user._id && // Ensure user has valid _id
      // Allow agents through, or filter out pending users
      ((user as any).isAgent || (user as any).type === 'agent') ||
      (!(user as any).isPending && (user as any).status !== 'pending')
    );

    // Special check for fareed@smartsaas.co.uk
    const fareedUser = activeUsers.find(u => u.email === 'fareed@smartsaas.co.uk');
    if (fareedUser) {
      console.log('✅ Found fareed@smartsaas.co.uk in activeUsers:', {
        _id: fareedUser._id,
        email: fareedUser.email,
        name: fareedUser.profile_information?.name,
        department: fareedUser.department || fareedUser.profile_information?.department
      });
    } else {
      console.warn('⚠️ fareed@smartsaas.co.uk NOT found in activeUsers!');
      // Check if it was in unwrappedUsers
      const fareedInUnwrapped = unwrappedUsers.find(u => u.email === 'fareed@smartsaas.co.uk' || u.user?.email === 'fareed@smartsaas.co.uk');
      console.warn('🔍 fareed@smartsaas.co.uk in unwrappedUsers:', {
        found: !!fareedInUnwrapped,
        email: fareedInUnwrapped?.email || fareedInUnwrapped?.user?.email,
        _id: fareedInUnwrapped?._id || fareedInUnwrapped?.user?._id,
        isPending: (fareedInUnwrapped as any)?.isPending,
        status: (fareedInUnwrapped as any)?.status
      });
    }

    // Only include owner if it exists and is valid
    if (owner && owner._id) {
      // Check if owner is already in activeUsers to avoid duplication
      const ownerInList = activeUsers.find(u => u._id === owner._id);
      if (ownerInList) {
        console.log('👑 Owner already in activeUsers, not adding separately');
        return activeUsers;
      }
      console.log('👑 Adding owner to allUsers:', {
        ownerId: owner._id,
        ownerEmail: owner.email,
        ownerDepartment: owner.profile_information?.department
      });
      return [owner, ...activeUsers];
    }

    return activeUsers;
  }, [initialUsers, userList, owner]);

  // Store department hierarchy levels (for custom hierarchy levels set via dialog)
  // MUST be defined before initializePositions and centerAllComponents which use it
  const departmentHierarchyLevelsRef = useRef<Record<string, number>>({});

  // Group users by department for visual organization
  const departmentGroups = useMemo(() => {
    return groupUsersByDepartment(allUsers);
  }, [allUsers]);

  // Removed console.log to reduce spam - uncomment for debugging
  // console.log('🔍 UserHierarchy display data:', {
  //   allUsersLength: allUsers.length,
  //   departmentGroups: departmentGroups.length,
  //   hasInitialUsers: !!initialUsers,
  //   initialUsersLength: initialUsers?.length || 0
  // });

  // AcademyCourseEditor pattern: Simple position handling
  const centerAllComponents = useCallback(() => {
    if (initialUsers && initialUsers.length > 0) {
      // Use positions from initialUsers (AcademyCourseEditor pattern)
      initialUsers.forEach(user => {
        if (user.x !== undefined && user.y !== undefined) {
          setComponentPosition(user._id, user.x, user.y);
        }
      });
    } else {
      // Fallback: Use hierarchical department-based positioning
      const hierarchicalPositions = calculateHierarchicalPositions(
        allUsers,
        allUsers[0],
        VIEWPORT_SIZE.width,
        VIEWPORT_SIZE.height,
        departmentHierarchyLevelsRef.current
      );
      Object.entries(hierarchicalPositions).forEach(([userId, position]) => {
        setComponentPosition(userId, position.x, position.y);
      });
    }

    // Force re-render to show new positions
    setTimeout(() => {
      setRenderKey(prev => prev + 1);
    }, 50);
  }, [allUsers, initialUsers, setComponentPosition]);

  // AcademyCourseEditor pattern: Initialize positions from initialUsers or calculate
  const initializePositions = useMemo(() => {
    if (initialUsers && initialUsers.length > 0) {
      // Use positions from initialUsers (AcademyCourseEditor pattern)
      const positions: Record<string, UserPosition> = {};
      initialUsers.forEach(user => {
        if (user.x !== undefined && user.y !== undefined) {
          positions[user._id] = { x: user.x, y: user.y };
          userPositionsRef.current[user._id] = { x: user.x, y: user.y };
          componentPositionsRef.current.set(user._id, { x: user.x, y: user.y });
        }
      });
      
      return positions;
    } else {
      // Fallback: Use hierarchical department-based positioning
      const hierarchicalPositions = calculateHierarchicalPositions(
        allUsers,
        allUsers[0],
        VIEWPORT_SIZE.width,
        VIEWPORT_SIZE.height,
        departmentHierarchyLevelsRef.current
      );
      Object.entries(hierarchicalPositions).forEach(([userId, position]) => {
        userPositionsRef.current[userId] = position;
        componentPositionsRef.current.set(userId, position);
      });
      
      return hierarchicalPositions;
    }
  }, [allUsers, initialUsers]);

  // Center the view on all components using correct moveTo formula
  const centerView = useCallback(() => {
    if (zoomableViewRef.current && allUsers.length > 0) {
      // Calculate the center of all user positions
      const positions = allUsers.map(user => userPositionsRef.current[user._id]).filter(Boolean);
      if (positions.length > 0) {
        const contentCenterX = positions.reduce((sum, pos) => sum + pos.x, 0) / positions.length;
        const contentCenterY = positions.reduce((sum, pos) => sum + pos.y, 0) / positions.length;

        // moveTo(offsetX, offsetY) sets the pan offset directly
        // Formula: offset = (viewportCenter / zoom) - contentTarget
        const currentZoom = zoomLevelRef.current || 0.6;
        const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenWidth / 2;
        const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenHeight / 2;

        // Center on the average card center (cards are 200x120)
        const cardCenterX = contentCenterX + 100; // pos.x + cardWidth/2
        const cardCenterY = contentCenterY + 60;  // pos.y + cardHeight/2

        const offsetX = (viewportCenterX / currentZoom) - cardCenterX;
        const offsetY = (viewportCenterY / currentZoom) - cardCenterY;

        if (__DEV__) {
          console.log('🎯 UserHierarchy centerView:', {
            contentCenter: { x: contentCenterX, y: contentCenterY },
            cardCenter: { x: cardCenterX, y: cardCenterY },
            zoom: currentZoom,
            viewportDimensions,
            viewportCenter: { x: viewportCenterX, y: viewportCenterY },
            calculatedOffset: { x: offsetX, y: offsetY }
          });
        }

        zoomableViewRef.current.moveTo(offsetX, offsetY);
      }
    }
  }, [allUsers.length, viewportDimensions, screenWidth, screenHeight]);

  // Auto-center on primary user (base component) when ready - Context.tsx pattern
  const hasRunAutoZoom = useRef(false); // Track if auto-zoom has already run to prevent loops
  const hasReceivedTransform = useRef(false); // Track if we've received the first transform event

  // Determine the user to center on - defaults to current user view
  const userToCenter = useMemo(() => {
    // 1. Explicit override via primaryUser prop
    if (primaryUser) return primaryUser;

    // 2. Default to current logged-in user (user view)
    if (profileData && profileData.id) {
      const currentUser = allUsers.find(u => u._id === profileData.id);
      if (currentUser) return currentUser;
    }

    // 3. Fallback to owner if current user not found
    if (owner && owner._id) return owner;

    // 4. Last resort: first user
    return allUsers[0];
  }, [primaryUser, owner, profileData, allUsers]);


  useEffect(() => {
    // Prevent running auto-zoom multiple times
    if (hasRunAutoZoom.current) {
      return;
    }

    // Wait for positioning to complete
    if (!positioningComplete) {
      if (__DEV__) {
        console.log('⏳ UserHierarchy: Waiting for positioning to complete...');
      }
      return;
    }

    if (!userToCenter) {
      if (__DEV__) {
        console.log('⏳ UserHierarchy: No user to center on');
      }
      return;
    }

    if (!zoomableViewRef.current) {
      if (__DEV__) {
        console.warn('⚠️ UserHierarchy: Positioning complete but zoomableViewRef.current is null/undefined');
      }
      return;
    }

    // Wait for viewport dimensions
    if (!viewportDimensions) {
      if (__DEV__) {
        console.log('⏳ UserHierarchy: Waiting for viewport dimensions...');
      }
      return;
    }

    // Check if user position is ready
    const userPosition = userPositionsRef.current[userToCenter._id];

    if (!userPosition) {
      if (__DEV__) {
        console.log('⏳ UserHierarchy: Waiting for user position...', {
          userId: userToCenter._id,
          totalPositions: Object.keys(userPositionsRef.current).length
        });
      }
      return;
    }

    // Mark as started
    hasRunAutoZoom.current = true;

    // Center on the user with a delay to ensure everything is rendered and zoom is set
    const centerTimeout = setTimeout(() => {
      try {
        const pos = userPositionsRef.current[userToCenter._id];
        if (!pos || !zoomableViewRef.current) {
          if (__DEV__) {
            console.warn('⚠️ UserHierarchy: Position or zoomableViewRef not available during centering');
          }
          return;
        }

        // Use actual zoom level - prefer state, then ref, then initialZoom (0.6)
        // The initialZoom is 0.6, so if transform hasn't fired, use that
        const currentZoom = hasReceivedTransform.current
          ? (zoomLevel || zoomLevelRef.current || 0.6)
          : 0.6; // Use initialZoom if transform hasn't fired yet

        const viewportCenterX = viewportDimensions.width / 2;
        const viewportCenterY = viewportDimensions.height / 2;

        // Center on the card's center (cards are 200x120)
        const cardCenterX = pos.x + 100; // pos.x + cardWidth/2
        const cardCenterY = pos.y + 60;  // pos.y + cardHeight/2

        // Formula: offset = (viewportCenter / zoom) - contentTarget
        const offsetX = (viewportCenterX / currentZoom) - cardCenterX;
        const offsetY = (viewportCenterY / currentZoom) - cardCenterY;

        if (__DEV__) {
          console.log('🎯 UserHierarchy: Auto-centering on user:', {
            userId: userToCenter._id,
            userEmail: userToCenter.email,
            position: pos,
            cardCenter: { x: cardCenterX, y: cardCenterY },
            zoom: currentZoom,
            zoomLevelState: zoomLevel,
            zoomLevelRef: zoomLevelRef.current,
            hasReceivedTransform: hasReceivedTransform.current,
            viewportDimensions,
            viewportCenter: { x: viewportCenterX, y: viewportCenterY },
            calculatedOffset: { x: offsetX, y: offsetY }
          });
        }

        // Use moveTo to center on user (Context.tsx pattern)
        if (zoomableViewRef.current.moveTo) {
          zoomableViewRef.current.moveTo(offsetX, offsetY);
        } else {
          console.error('❌ UserHierarchy: moveTo method not available');
        }
      } catch (error) {
        console.error('❌ UserHierarchy: Error centering view:', error);
        // Reset flag on error so it can retry
        hasRunAutoZoom.current = false;
      }
    }, 800); // Increased delay to ensure transform event has fired and zoom is set

    return () => clearTimeout(centerTimeout);
  }, [positioningComplete, userToCenter, viewportDimensions]);

  useEffect(() => {
    // CRITICAL FIX: Don't overwrite existing positions, only initialize missing ones
    // This prevents drag updates from being lost when parent re-renders
    Object.entries(initializePositions).forEach(([userId, position]) => {
      // Only set initial position if this user doesn't already have a position
      if (!userPositionsRef.current[userId]) {
        userPositionsRef.current[userId] = position;
        componentPositionsRef.current.set(userId, position);
      }
    });
  }, [initializePositions]);

  // Imperative positioning: Set positions on draggable components after they mount
  useEffect(() => {
    const setImperativePositions = () => {
      Object.entries(initializePositions).forEach(([userId, position]) => {
        const componentRef = draggableComponentRefs.current.get(userId);
        const hasExistingPosition = !!userPositionsRef.current[userId];

        // Only set position if component exists and no existing position
        if (componentRef && !hasExistingPosition) {
          componentRef.setPosition(position);
        }
      });
    };

    // Small delay to ensure components are mounted
    const timeoutId = setTimeout(setImperativePositions, 50);

    return () => clearTimeout(timeoutId);
  }, [initializePositions, allUsers.length]);

  // Monitor when all component refs are ready
  useEffect(() => {
    const checkRefsReady = () => {
      const expectedUserCount = allUsers.length;
      const actualRefCount = draggableComponentRefs.current.size;

      if (expectedUserCount > 0 && actualRefCount >= expectedUserCount) {
        setRefsReady(true);
      } else {
        setRefsReady(false);
      }
    };

    // Check immediately and also with a small delay
    checkRefsReady();
    const timeoutId = setTimeout(checkRefsReady, 100);

    return () => clearTimeout(timeoutId);
  }, [allUsers.length]);

  // Execute imperative positioning when refs are ready
  useEffect(() => {
    // PREVENT LOOPS: Only run once per component lifecycle
    if (hasRunImperativePositioning.current) {
      return;
    }

    // CRITICAL FIX: Don't run imperative positioning during drag operations
    // Note: DraggableHierarchyItem manages its own drag state internally

    if (!refsReady) {
      return;
    }

    if (Object.keys(initializePositions).length === 0) {
      return;
    }

    let positionsSet = 0;
    Object.entries(initializePositions).forEach(([userId, position]) => {
      const componentRef = draggableComponentRefs.current.get(userId);
      const hasExistingPosition = !!userPositionsRef.current[userId];

      // OPTIMIZED: Only call setPosition if no existing position to prevent resets
      if (componentRef && !hasExistingPosition) {
        componentRef.setPosition(position);
        positionsSet++;
      }
    });

    // Mark as completed to prevent re-runs
    hasRunImperativePositioning.current = true;

    if (positionsSet > 0) {
      // Force a re-render of all components by updating the render key
      setTimeout(() => {
        setRenderKey(prev => prev + 1);
        setPositioningComplete(prev => !prev);
      }, 100);
    }
  }, [refsReady, initializePositions, renderKey]);

  // Auto-initialize component positions on mount with proper centering
  // Only auto-center if NO primaryUser (otherwise primary user centering takes precedence)
  useEffect(() => {
    if (!isLoading && allUsers.length > 0 && !primaryUser) {
      const timer = setTimeout(() => {
        centerAllComponents();

        // Also center the view after positioning components
        setTimeout(() => {
          centerView();
        }, 200);
      }, 500); // Increased delay to ensure all components are rendered

      return () => clearTimeout(timer);
    }
  }, [isLoading, allUsers.length, primaryUser, centerAllComponents, centerView]);

  // Debounced position update handler with network communication
  const debouncedPositionUpdate = useCallback(
    debounce((userId: string, position: UserPosition) => {
      if (!isComponentMounted) {
        return;
      }

      // Network communication would go here:
      // socket?.emit('commitTranslate', {
      //   userId,
      //   translateX: position.x,
      //   translateY: position.y,
      //   companyId: props.companyId
      // });
    }, 300), // Final update after 300ms of inactivity
    [isComponentMounted]
  );

  // Function to get all user positions for arrow detection
  // Stabilized with empty dependency array to prevent recreating function
  const getAllUserPositions = useCallback(() => {
    const positions = { ...userPositionsRef.current };

    // If positions are empty but we have users, force populate with current positions
    if (Object.keys(positions).length === 0) {
      draggableComponentRefs.current.forEach((componentRef, userId) => {
        if (componentRef) {
          const pos = componentRef.getPosition();
          positions[userId] = pos;
          userPositionsRef.current[userId] = pos;
        }
      });
    }

    return positions;
  }, []); // Empty array - function uses refs only

  // Arrow handler - receives arrow data from any DraggableUserCard
  // CRITICAL: Fully ref-based - NO state updates, NO parent re-renders
  const handleArrowUpdate = useCallback((arrowData: ArrowData) => {
    // Validate coordinates before storing
    const isValidNumber = (n: number) => typeof n === 'number' && !isNaN(n) && isFinite(n);

    if (!arrowData.reset && (!isValidNumber(arrowData.startX) || !isValidNumber(arrowData.startY) ||
      !isValidNumber(arrowData.endX) || !isValidNumber(arrowData.endY))) {
      console.warn('⚠️ Invalid arrow coordinates, skipping:', arrowData.id, {
        startX: arrowData.startX,
        startY: arrowData.startY,
        endX: arrowData.endX,
        endY: arrowData.endY
      });
      return;
    }

    // Update ref immediately (NO parent state update!)
    if (arrowData.reset) {
      arrowsRef.current.delete(arrowData.id);
    } else {
      arrowsRef.current.set(arrowData.id, arrowData);
    }

    // Trigger arrow renderer to update via imperative handle
    // This updates state INSIDE arrow renderer, NOT in parent
    if (arrowRendererRef.current?.forceUpdate) {
      arrowRendererRef.current.forceUpdate();
    }
  }, []); // EMPTY DEPS - function uses only refs

  // AcademyCourseEditor pattern: Simple position change handler
  const handlePositionChange = useCallback((userId: string, newPosition: UserPosition) => {
    // Here you would save the position to your backend
    // updateUserPosition(userId, newPosition);

    // Update position refs for real-time updates
    userPositionsRef.current[userId] = newPosition;
    componentPositionsRef.current.set(userId, newPosition);

    // Trigger LinkHandler update
    if (linkHandlerRef.current?.forceUpdate) {
      linkHandlerRef.current.forceUpdate();
    }

    // Debounced network update
    debouncedPositionUpdate(userId, newPosition);
  }, [debouncedPositionUpdate]);

  // Store allUsers in a ref to avoid dependency on state during drag
  const allUsersRef = useRef(allUsers);
  useEffect(() => {
    allUsersRef.current = allUsers;
  }, [allUsers]);

  // Handle department drag - move all users in the department
  // CRITICAL: No state updates, only ref updates and imperative handles
  const handleDepartmentDrag = useCallback((departmentName: string, deltaX: number, deltaY: number) => {
    // Find all users in this department - use ref to avoid re-renders
    const departmentUsers = allUsersRef.current.filter(user => {
      let userDept = user.department || user.profile_information?.department;
      // Normalize: treat missing/null/undefined/empty as "Unassigned"
      if (!userDept || userDept === '' || userDept === null || userDept === undefined) {
        userDept = 'Unassigned';
      }
      return userDept === departmentName;
    });

    // Update position for each user in the department - ONLY ref updates, NO state
    // CRITICAL: Use setPositionSilent instead of setPosition to avoid state updates
    departmentUsers.forEach(user => {
      const componentRef = draggableComponentRefs.current.get(user._id);
      if (componentRef) {
        // Get current position from component itself (most accurate)
        const currentPos = componentRef.getPosition();

        if (currentPos) {
          const newPosition = {
            x: currentPos.x + deltaX,
            y: currentPos.y + deltaY
          };

          // Update refs immediately (NO state update)
          userPositionsRef.current[user._id] = newPosition;
          componentPositionsRef.current.set(user._id, newPosition);

          // Update draggable component position via silent method (NO state update)
          // setPositionSilent updates shared values without calling onPositionChange
          if (componentRef.setPositionSilent) {
            componentRef.setPositionSilent(newPosition);
          }
        }
      }
    });

    // Trigger updates via imperative handles only (these update internal state, not parent)
    // These do NOT cause parent re-renders
    // Note: LinkHandler and ArrowRenderer use debounced updates internally, so safe to call
    if (linkHandlerRef.current?.forceUpdate) {
      linkHandlerRef.current.forceUpdate();
    }

    if (arrowRendererRef.current?.forceUpdate) {
      arrowRendererRef.current.forceUpdate();
    }

    // DO NOT call departmentGroupingRef.forceUpdate during drag
    // Even with debouncing, calling forceUpdate during drag can cause state updates that interrupt the gesture
    // DepartmentGrouping reads positions from ref directly, so borders will update on next natural render
    // We'll update it on drag end instead
  }, []); // EMPTY DEPS - uses refs only, no state dependencies

  // Handle department drag end - commit final positions
  const handleDepartmentDragEnd = useCallback((departmentName: string) => {
    // Find all users in this department - use allUsers from closure, no state dependency
    const departmentUsers = allUsers.filter(user => {
      let userDept = user.department || user.profile_information?.department;
      // Normalize: treat missing/null/undefined/empty as "Unassigned"
      if (!userDept || userDept === '' || userDept === null || userDept === undefined) {
        userDept = 'Unassigned';
      }
      return userDept === departmentName;
    });

    // Commit final positions for each user
    departmentUsers.forEach(user => {
      const finalPos = userPositionsRef.current[user._id];
      if (finalPos) {
        // Debounced network update for each user
        debouncedPositionUpdate(user._id, finalPos);
      }
    });

    // Update DepartmentGrouping borders after drag ends (safe to update state now)
    if (departmentGroupingRef.current?.forceUpdate) {
      departmentGroupingRef.current.forceUpdate();
    }
  }, [allUsers, debouncedPositionUpdate]);

  // Handle department rename - update all users in the department
  const handleDepartmentRename = useCallback((oldName: string, newName: string) => {
    if (!newName || !newName.trim() || newName === oldName) {
      return;
    }

    // Find all users in this department
    const departmentUsers = allUsers.filter(user => {
      let userDept = user.department || user.profile_information?.department;
      // Normalize: treat missing/null/undefined/empty as "Unassigned"
      if (!userDept || userDept === '' || userDept === null || userDept === undefined) {
        userDept = 'Unassigned';
      }
      return userDept === oldName;
    });

    // Update department name for all users in the department
    // Note: This is a local update - you may want to persist this to backend
    departmentUsers.forEach(user => {
      // Update the user object's department field
      if (user.profile_information) {
        user.profile_information.department = newName;
      } else {
        user.profile_information = { department: newName };
      }
      user.department = newName;
    });

    // Update hierarchy level mapping if it exists
    if (departmentHierarchyLevelsRef.current[oldName] !== undefined) {
      departmentHierarchyLevelsRef.current[newName] = departmentHierarchyLevelsRef.current[oldName];
      delete departmentHierarchyLevelsRef.current[oldName];
    }

    // Trigger refresh to update UI
    refresh();

    // Recalculate positions with new department name
    setTimeout(() => {
      centerAllComponents();
      setTimeout(() => {
        centerView();
      }, 200);
    }, 100);
  }, [allUsers, refresh, centerAllComponents, centerView]);

  // Handle department hierarchy level change
  const handleDepartmentHierarchyChange = useCallback((departmentName: string, hierarchyLevel: number) => {
    console.log('📊 Changing department hierarchy level:', { departmentName, hierarchyLevel });

    // Store the custom hierarchy level
    departmentHierarchyLevelsRef.current[departmentName] = hierarchyLevel;

    // Recalculate positions with new hierarchy level
    setTimeout(() => {
      centerAllComponents();
      setTimeout(() => {
        centerView();
      }, 200);
    }, 100);
  }, [centerAllComponents, centerView]);

  // Network event handler
  const handleNetworkPositionUpdate = useCallback((data: any) => {
    if (!isComponentMounted) return;
    userPositionsRef.current = {
      ...userPositionsRef.current,
      [data.userId]: { x: data.translateX, y: data.translateY }
    };
  }, [isComponentMounted]);

  // Component lifecycle and network setup
  useEffect(() => {
    setIsComponentMounted(true);

    // Setup network connections
    const setupNetwork = () => {
      // Example socket setup:
      // socket?.emit('join', { userId: profileData?.id, companyId: props.companyId });
      // socket?.on('userMove', handleNetworkPositionUpdate);
      // socket?.on('userTranslate', handleNetworkPositionUpdate);

      return () => {
        // socket?.emit('leave');
        // socket?.off('userMove', handleNetworkPositionUpdate);
        // socket?.off('userTranslate', handleNetworkPositionUpdate);
      };
    };

    networkCleanupRef.current = setupNetwork();

    return () => {
      setIsComponentMounted(false);

      // Cancel pending debounced calls
      debouncedPositionUpdate.cancel();

      // Run network cleanup
      if (networkCleanupRef.current) {
        networkCleanupRef.current();
      }
    };
  }, [debouncedPositionUpdate, handleNetworkPositionUpdate, profileData?.id]);



  // Expose imperative methods via ref
  useImperativeHandle(ref, () => ({
    setComponentPosition,
    getComponentPosition,
    centerAllComponents,
    centerView,
    // Expose zoomable view methods
    moveTo: (x: number, y: number) => zoomableViewRef.current?.moveTo(x, y),
    zoomTo: (zoom: number, zoomCenter: { x: number, y: number }) => zoomableViewRef.current?.zoomTo(zoom, zoomCenter),
    zoomBy: (delta: number) => zoomableViewRef.current?.zoomBy(delta),
    // Get current zoom info - use ref for accuracy
    getCurrentZoom: () => zoomLevelRef.current,
    // Get viewport info
    getViewportSize: () => VIEWPORT_SIZE,
    // Force re-render
    forceUpdate: () => setIsComponentMounted(prev => !prev),
  }), [setComponentPosition, getComponentPosition, centerAllComponents, centerView]);

  // Debug: Log rendering state
  useEffect(() => {
    console.log('🎬 UserHierarchy Render State:', {
      allUsersCount: allUsers.length,
      allUsers: allUsers.map(u => ({ 
        id: u._id, 
        email: u.email, 
        name: u.profile_information?.name,
        department: u.department || u.profile_information?.department,
        hasPosition: !!userPositionsRef.current[u._id]
      })),
      isLoading,
      hasInitialUsers: !!initialUsers,
      initialUsersCount: initialUsers?.length || 0,
      userListCount: userList?.length || 0,
      userListSample: userList?.slice(0, 2).map(u => ({
        hasUser: !!u.user,
        userId: u.user?._id,
        userEmail: u.user?.email,
        structure: u.user ? 'nested' : 'direct'
      })),
      hasOwner: !!owner,
      ownerId: owner?._id,
      ownerEmail: owner?.email,
      positionsCount: Object.keys(userPositionsRef.current).length,
      componentPositionsCount: componentPositionsRef.current.size,
      initializePositionsCount: Object.keys(initializePositions).length,
      departmentGroupsCount: departmentGroups.length,
      departmentGroups: departmentGroups.map(dg => ({
        name: dg.name,
        count: dg.count,
        users: dg.users?.map(u => ({
          id: u._id || u.user?._id,
          email: u.email || u.user?.email,
          name: u.profile_information?.name || u.user?.profile_information?.name
        }))
      }))
    });
  }, [allUsers.length, isLoading, initialUsers, userList, owner, departmentGroups]);

  // Show loading state
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <Text userSelect='none'>Loading hierarchy...</Text>
      </View>
    );
  }

  // Don't render users until we have users
  if (allUsers.length === 0) {
    console.warn('⚠️ UserHierarchy: No users to display');
    return (
      <Pressable style={[getModernCardStyle(theme, 'medium'), {
        maxHeight: 260, top: "30%", maxWidth: "80%", alignSelf: "center", width: '100%',
        flex: 1, gap: 20, justifyContent: 'center', alignItems: 'center'
      }]}>
        <AvatarHandler avatar="" />
        <Text userSelect='none'>No team members found</Text>
        <Button onPress={() => {
          refresh?.();
        }}>
          <Text userSelect='none'>Refresh</Text>
        </Button>
      </Pressable>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      {/* Department Legend */}
      <DepartmentLegend
        departmentGroups={departmentGroups}
        theme={theme}
        position={Platform.select({ native: 'bottom-left' })}
      />

      <ReactNativeZoomableView
        ref={zoomableViewRef}
        maxZoom={2}
        minZoom={0.3}
        initialZoom={0.6}
        contentWidth={VIEWPORT_SIZE.width}
        contentHeight={VIEWPORT_SIZE.height}
        initialOffsetX={calculatedOffsetX}
        initialOffsetY={calculatedOffsetY}
        panBoundaryPadding={200}
        onLayout={(event) => {
          const { width, height } = event.nativeEvent.layout;
          setViewportDimensions({ width, height });
        }}
        onTransform={(event) => {
          // Update both state and ref
          setZoomLevel(event.zoomLevel ?? 1);
          zoomLevelRef.current = event.zoomLevel ?? 1;
          hasReceivedTransform.current = true; // Mark that we've received transform event

          // Safety: Ensure offset values are valid numbers
          const offsetX = typeof event.offsetX === 'number' && !isNaN(event.offsetX) ? -event.offsetX : 0;
          const offsetY = typeof event.offsetY === 'number' && !isNaN(event.offsetY) ? -event.offsetY : 0;
          setGridOffsetX(offsetX);
          setGridOffsetY(offsetY);
        }}
        style={{ flex: 1 }}
      >
        <View style={{ width: VIEWPORT_SIZE.width, height: VIEWPORT_SIZE.height, backgroundColor: 'transparent' }}>
          {/* Background */}
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: VIEWPORT_SIZE.width,
            height: VIEWPORT_SIZE.height,
            backgroundColor: theme ? '#f9f9f9' : '#1a1a1a',
            opacity: 0.3
          }} />

          {/* Infinite Grid - Performant grid with viewport culling */}
          {screenWidth > 0 && screenHeight > 0 && Platform.OS != 'android' && (
            <InfiniteGrid
              zoomLevel={zoomLevel}
              offsetX={gridOffsetX}
              offsetY={gridOffsetY}
              viewportWidth={screenWidth}
              viewportHeight={screenHeight}
              cellSize={GRID_SIZE}
              dotMode={false}
              color={theme ? '#cccccc' : '#444444'}
              opacity={Math.max(0.3, Math.min(0.8, zoomLevel * 0.5 + 0.3))}
            />
          )}

          {/* Enhanced LinkHandler for real-time updates */}
          <UserLinkHandler
            ref={linkHandlerRef}
            userPositionsRef={userPositionsRef}
            links={props.linkList || []}
            theme={theme}
            zoomLevel={zoomLevel}

            transformFactor={1}
            linkDeleteCallback={(linkId) => {
              // Handle link deletion here
            }}
          />

          {/* Optimized Connection Lines */}
          <ConnectionLines
            connections={props.linkList || []}
            userPositions={userPositionsRef.current}
            theme={theme}
            zoomLevel={zoomLevel}
          />

          {/* Department Grouping Overlay */}
          <DepartmentGrouping
            ref={departmentGroupingRef}
            departmentGroups={departmentGroups}
            userPositionsRef={userPositionsRef}
            theme={theme}
            zoomLevel={zoomLevel}
            onDepartmentDrag={handleDepartmentDrag}
            onDepartmentDragEnd={handleDepartmentDragEnd}
            onDepartmentRename={handleDepartmentRename}
            onDepartmentHierarchyChange={handleDepartmentHierarchyChange}
          />

          {/* Users - Always render, use fallback positions if needed */}
          {allUsers.map((user, index) => {
            // Get position from ref first, then from calculated positions
            const refPosition = userPositionsRef.current[user._id];
            const calculatedPosition = initializePositions[user._id];

            // Use calculated position, or fallback to center if not ready yet
            const initialPosition = refPosition || calculatedPosition || {
              x: VIEWPORT_SIZE.width / 2,
              y: VIEWPORT_SIZE.height / 2
            };



            return (
              <DraggableUserCard
                key={`${user._id}-${renderKey}`} // Force re-render with key
                ref={(ref) => {
                  if (ref) {
                    draggableComponentRefs.current.set(user._id, ref);

                    // Check if all refs are now ready
                    if (draggableComponentRefs.current.size >= allUsers.length) {
                      // Use setTimeout to ensure this happens after render
                      setTimeout(() => {
                        setRefsReady(true);
                      }, 10);
                    }
                  } else {
                    draggableComponentRefs.current.delete(user._id);
                  }
                }}
                user={user}
                initialPosition={initialPosition}
                onPositionChange={handlePositionChange}
                onPositionUpdate={(itemId, position) => {
                  // Update position for real-time tracking (continuous updates)
                  userPositionsRef.current[itemId] = position;
                  componentPositionsRef.current.set(itemId, position);
                }}
                onSnap={props.handleSnap}
                getAllUserPositions={getAllUserPositions}
                arrowHandler={handleArrowUpdate}
                dragEnabled={true}
                zoomLevel={zoomLevel}
                itemWidth={200}
                itemHeight={120}
                borderRadius={12}
                shadowEnabled={true}
                onPress={onUserPress}
              >
                {({ isDragging: itemDragging }) => (
                  <UserCard
                    user={user}
                    theme={theme}
                    isDragging={itemDragging}
                    showArrows={true}
                    portrait={true}
                  />
                )}
              </DraggableUserCard>
            );
          })}

          {/* Centralized Arrow Renderer - ISOLATED to prevent parent re-renders */}
          {/* State is managed INSIDE renderer via imperative handle */}
          {/* Parent has ZERO state updates related to arrows */}
          <HierarchyArrowRenderer
            ref={arrowRendererRef}
            arrowsRef={arrowsRef}
            width={VIEWPORT_SIZE.width}
            height={VIEWPORT_SIZE.height}
            zIndex={100}
          />

        </View>
      </ReactNativeZoomableView>
    </View>
  );
});

UserHierarchy.displayName = 'UserHierarchy';

export default UserHierarchy;
